// Keyboard-shortcut bridge.
//
// Electron uses a native menu bar whose accelerators forward to
// `menu:*` IPC events the renderer subscribes to via onMenuAction.
// On web we attach a single global keydown listener and translate the
// same key combos into the same `menu:*` event names — so the
// renderer's existing onMenuAction handler runs unchanged.
//
// A full in-app menu bar UI is a UX milestone for later; the shortcuts
// alone give the keyboard-driven user parity.

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform || '');

// Each entry: [accelerator, menuChannel].
// Accelerator format mirrors Electron's: ⌘/Ctrl is "Mod", Shift is "S",
// Alt/Option is "Alt". We list the keys in a canonical order
// (Mod-S-Alt-letter) so the matcher can do a direct string compare.
const SHORTCUTS = [
  ['Mod+N', 'menu:new'],
  ['Mod+S+N', 'menu:new-from-template'],
  ['Mod+Alt+O', 'menu:open-cloud'],
  ['Mod+S+O', 'menu:open-cloud'],
  ['Mod+O', 'menu:open-cloud'],
  ['Mod+W', 'menu:close'],
  ['Mod+S', 'menu:save'],
  ['Mod+S+S', 'menu:save-as'],
  ['Mod+S+P', 'menu:export-pdf'],
  ['Mod+P', 'menu:print'],
  ['Mod+F', 'menu:find'],
  ['Mod+I', 'menu:title-page'],
  ['Mod+S+A', 'menu:toggle-ai'],
  ['Mod+S+L', 'menu:open-cloud'],
  ['Mod+S+T', 'menu:toggle-timeline'],
  [isMac ? 'Mod+,' : 'Mod+,', 'menu:settings'],
];

function describe(ev) {
  const parts = [];
  if (isMac ? ev.metaKey : ev.ctrlKey) parts.push('Mod');
  if (ev.shiftKey) parts.push('S');
  if (ev.altKey) parts.push('Alt');
  let key = ev.key;
  if (!key) return '';
  if (key.length === 1) key = key.toUpperCase();
  // Skip pure modifier keystrokes.
  if (key === 'Meta' || key === 'Control' || key === 'Shift' || key === 'Alt') return '';
  parts.push(key);
  return parts.join('+');
}

function shouldSkip(ev) {
  // Don't intercept inside text inputs / textareas / contenteditable
  // unless the shortcut is one of the few that ALWAYS wins (Save,
  // Find, Settings, Print). Otherwise users can't type normally.
  const t = ev.target;
  const inTextField =
    t &&
    (t.tagName === 'INPUT' ||
      t.tagName === 'TEXTAREA' ||
      (t.isContentEditable && !t.classList?.contains('screenplay-page')));
  return !!inTextField;
}

const ALWAYS_FIRE = new Set([
  'menu:save',
  'menu:save-as',
  'menu:find',
  'menu:settings',
  'menu:print',
  'menu:export-pdf',
]);

export function buildMenuHandlers({ bus }) {
  function handleKeydown(ev) {
    const accel = describe(ev);
    if (!accel) return;
    const match = SHORTCUTS.find(([a]) => a === accel);
    if (!match) return;
    const [, channel] = match;
    if (shouldSkip(ev) && !ALWAYS_FIRE.has(channel)) return;
    ev.preventDefault();
    bus.emit(channel);
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('keydown', handleKeydown, { capture: true });
  }

  // No request/response methods to expose — onMenuAction is wired in
  // bridge/index.js via the same `menu:*` channels this module emits.
  return {};
}
